// src/components/AppLauncher/TaskBar.js
import React, { useState, useRef, useEffect } from 'react';
import { combineThemeClasses } from '../../utils/theme';
import { MULTI_WINDOW_LAYOUTS } from '../../utils/layoutManager';
import { Layout } from 'lucide-react';

const TaskBar = ({
  activeApps,
  windowStates,
  focusedWindowId,
  toggleMinimize,
  bringWindowToFront,
  snapViewportToApp,
  selectedAppIds,
  setSelectedAppIds,
  applyLayoutToSelectedApps,
  closeWindow,
  theme = 'dark'
}) => {
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const menuRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current.contains(event.target) && !menuRef.current?.contains(event.target)) {
        if (selectionMode) {
          setSelectionMode(false);
          setSelectedAppIds([]);
          setShowLayoutMenu(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectionMode) {
        setSelectionMode(false);
        setSelectedAppIds([]);
        setShowLayoutMenu(false);
      }
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectionMode, setSelectedAppIds]);

  const handleAppClick = (app, e) => {
    if (selectionMode) {
      // toggle checkbox
      if (selectedAppIds.includes(app.id)) {
        setSelectedAppIds(selectedAppIds.filter(id => id !== app.id));
      } else {
        setSelectedAppIds([...selectedAppIds, app.id]);
      }
    } else {
      if (e.shiftKey) {
        // Enter selection mode
        setSelectionMode(true);
        setSelectedAppIds([app.id]);
      } else {
        // Normal click: focus the app
        setSelectedAppIds([]);
        const state = windowStates[app.id];
        if (state?.isMinimized) {
          toggleMinimize(app.id);
        }
        bringWindowToFront(app.id);
      }
    }
  };

  const handleAppDoubleClick = (app) => {
    if (!selectionMode) {
      snapViewportToApp(app.id);
    }
  };

  const handleAppClose = (appId) => {
    if (typeof closeWindow === 'function') {
      closeWindow(appId);
    } else {
      console.warn('closeWindow function not provided to TaskBar');
    }
  };

  return (
    <div className="flex items-center space-x-2 relative px-2" ref={containerRef}>
      {activeApps.map(app => {
        const isSelected = selectedAppIds.includes(app.id);
        return (
          <div key={app.id} className="flex items-center space-x-1">
            {selectionMode && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {
                  if (isSelected) {
                    setSelectedAppIds(selectedAppIds.filter(id => id !== app.id));
                  } else {
                    setSelectedAppIds([...selectedAppIds, app.id]);
                  }
                }}
                className="form-checkbox h-4 w-4 text-blue-500"
              />
            )}
            <div className={`flex items-center ${combineThemeClasses(theme, ['menuBar.button.base', focusedWindowId === app.id ? 'menuBar.button.active' : 'menuBar.button.hover'])} ${isSelected ? 'border-2 border-blue-500' : ''}`}>
              <button
                onClick={(e) => handleAppClick(app, e)}
                onDoubleClick={() => handleAppDoubleClick(app)}
                className="px-3 py-1"
              >
                {app.name}
              </button>
              <button
                onClick={() => handleAppClose(app.id)}
                className="px-2 text-black text-lg font-bold hover:bg-gray-200"
                title="Close App"
                style={{ lineHeight: '1' }}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}

      {selectionMode && selectedAppIds.length > 0 && (
        <div className="flex items-center ml-4 relative" ref={menuRef}>
          <button
            className="flex items-center px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600"
            onClick={() => setShowLayoutMenu(!showLayoutMenu)}
          >
            <Layout size={16} />
          </button>
          {showLayoutMenu && (
            <div className="absolute top-full right-0 bg-gray-800 border border-gray-700 rounded shadow-lg mt-1 py-1 z-50 w-48">
              {MULTI_WINDOW_LAYOUTS.map(layout => (
                <button
                  key={layout.name}
                  className="block w-full text-left px-3 py-1 text-slate-300 hover:bg-slate-700"
                  onClick={() => {
                    applyLayoutToSelectedApps(layout.name, selectedAppIds);
                    setShowLayoutMenu(false);
                  }}
                >
                  {layout.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskBar;
